/**
 * ConfirmDialog — the gate in front of anything the operator cannot undo.
 *
 * In this console that is: revoking a grant (a client's link dies on their next click),
 * deleting a schedule or recipient, and firing a real dispatch from Run now. Use it for
 * those; do not use it for saves.
 *
 *   <ConfirmDialog
 *     open={revoking !== null}
 *     tone="danger"
 *     title="Revoke this link?"
 *     confirmLabel="Revoke link"
 *     consequence="The recipient gets a 410 on their next click. Re-issuing mints a new token; it does not restore this one."
 *     confirmPhrase={grant.recipient_email}      // typed confirmation for the loud ones
 *     onConfirm={async () => { await api.grants.revoke(hash); }}
 *     onCancel={() => setRevoking(null)}
 *   />
 *
 * `onConfirm` may return a promise: the confirm button shows a spinner until it settles,
 * the dialog stays open on rejection (so the operator sees the error in a toast and can
 * retry), and closes on success.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import Button from './Button';
import Modal from './Modal';

export type ConfirmTone = 'danger' | 'warn' | 'default';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** Body copy. Say what is about to happen in plain words. */
  children?: ReactNode;
  /**
   * A single sentence naming the irreversible consequence. Rendered as a callout —
   * this is the line that stops someone revoking the wrong client's link.
   */
  consequence?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * When set, the operator must type this exact string to enable the confirm button.
   * Use the thing being destroyed (a client id, a recipient email) for loud actions.
   */
  confirmPhrase?: string;
  /** May return a promise; the button spins until it settles. */
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
}

const TONE_GLYPH: Record<ConfirmTone, string> = {
  danger: '✕',
  warn: '▲',
  default: 'i',
};

export function ConfirmDialog({
  open,
  title,
  children,
  consequence,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  confirmPhrase,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');

  // Reset between openings so a previous typed phrase never carries over.
  useEffect(() => {
    if (open) {
      setTyped('');
      setBusy(false);
    }
  }, [open]);

  const phraseSatisfied = !confirmPhrase || typed.trim() === confirmPhrase;

  const handleConfirm = async (): Promise<void> => {
    if (!phraseSatisfied || busy) return;
    try {
      setBusy(true);
      await onConfirm();
      setBusy(false);
    } catch {
      // Leave the dialog open: the caller surfaces the error, the operator retries.
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onCancel}
      dismissible={!busy}
      size="sm"
      title={title}
      hideClose={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            loading={busy}
            disabled={!phraseSatisfied}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        {children ? <div>{children}</div> : null}

        {consequence ? (
          <div className={`callout tone-${tone === 'default' ? 'info' : tone}`}>
            <span className="callout-glyph" aria-hidden="true">
              {TONE_GLYPH[tone]}
            </span>
            <span>{consequence}</span>
          </div>
        ) : null}

        {confirmPhrase ? (
          <label className="field">
            <span className="field-label">
              Type <code>{confirmPhrase}</code> to confirm
            </span>
            <input
              className="input is-mono"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && phraseSatisfied) void handleConfirm();
              }}
            />
          </label>
        ) : null}
      </div>
    </Modal>
  );
}

export default ConfirmDialog;
